import type { OcrWord } from 'features/files/hooks/usePageOcr'
import * as React from 'react'
import styled from 'styled-components'

interface OcrTextOverlayProps {
  words: readonly OcrWord[]
  /**
   * The CSS width of the rendered PDF page (i.e. the `width` prop passed to
   * react-pdf's <Page>). This is the exact value pdf.js used when sizing the
   * visible canvas, so `scale = displayWidth / ocrSourceWidth` is always
   * correct — no canvas ref or ResizeObserver required.
   */
  displayWidth: number
  /** Width of the offscreen canvas that Tesseract processed */
  ocrSourceWidth: number
  /**
   * When true, renders the recognized text visibly so you can see exactly
   * what Tesseract found and where it positioned each word.
   */
  debug?: boolean
}

/**
 * Sort words into reading order (top-to-bottom, left-to-right) and group
 * them into lines based on vertical proximity. Words whose vertical centres
 * are within `lineThreshold` OCR-pixels of each other are on the same line.
 */
function toReadingOrder(
  words: readonly OcrWord[],
  lineThreshold: number
): ReadonlyArray<OcrWord & { isLineStart: boolean }> {
  if (words.length === 0) return []

  // Sort by vertical centre, then left edge
  const sorted = [...words].sort((a, b) => {
    const aCenterY = (a.bbox.y0 + a.bbox.y1) / 2
    const bCenterY = (b.bbox.y0 + b.bbox.y1) / 2
    if (Math.abs(aCenterY - bCenterY) > lineThreshold)
      return aCenterY - bCenterY
    return a.bbox.x0 - b.bbox.x0
  })

  return sorted.map((word, i) => {
    if (i === 0) return { ...word, isLineStart: false }
    const prevCenterY = (sorted[i - 1].bbox.y0 + sorted[i - 1].bbox.y1) / 2
    const currCenterY = (word.bbox.y0 + word.bbox.y1) / 2
    return {
      ...word,
      isLineStart: Math.abs(currCenterY - prevCenterY) > lineThreshold,
    }
  })
}

export const OcrTextOverlay: React.FC<OcrTextOverlayProps> = ({
  words,
  displayWidth,
  ocrSourceWidth,
  debug = false,
}) => {
  if (ocrSourceWidth === 0 || displayWidth === 0) return null

  const scale = displayWidth / ocrSourceWidth

  // Estimate line height from median bbox height — used to group words into lines
  const heights = words.map((w) => w.bbox.y1 - w.bbox.y0).sort((a, b) => a - b)
  const medianHeight = heights[Math.floor(heights.length / 2)] ?? 20
  const lineThreshold = medianHeight * 0.6

  const ordered = toReadingOrder(words, lineThreshold)

  return (
    <Overlay>
      {ordered.map((word, i) => {
        const left = word.bbox.x0 * scale
        const top = word.bbox.y0 * scale
        const wordWidth = (word.bbox.x1 - word.bbox.x0) * scale
        const wordHeight = (word.bbox.y1 - word.bbox.y0) * scale
        return (
          <React.Fragment key={i}>
            {/* Inject a newline span at line boundaries so copied text has
                real line breaks instead of everything running together */}
            {word.isLineStart && <NewlineSpan>{'\n'}</NewlineSpan>}
            <WordSpan
              $debug={debug}
              title={
                debug
                  ? `"${word.text}"  conf: ${Math.round(word.confidence)}`
                  : undefined
              }
              style={{
                left,
                top,
                width: wordWidth,
                height: wordHeight,
                fontSize: debug
                  ? Math.min(wordHeight * 0.75, 9)
                  : wordHeight * 0.85,
              }}>
              {/* Trailing space is invisible (overflow:hidden clips it) but
                  gets included in clipboard copy so words don't run together */}
              {word.text + ' '}
            </WordSpan>
          </React.Fragment>
        )
      })}
    </Overlay>
  )
}

OcrTextOverlay.displayName = 'OcrTextOverlay'

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 2;
  overflow: visible;
`

const NewlineSpan = styled.span`
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;
  user-select: text;
  pointer-events: none;
  font-size: 0;
`

const WordSpan = styled.span<{ $debug: boolean }>`
  position: absolute;
  white-space: pre;
  line-height: 1;
  cursor: text;
  pointer-events: auto;
  user-select: text;
  overflow: hidden;

  color: ${({ $debug }) => ($debug ? 'rgba(180, 0, 0, 0.85)' : 'transparent')};
  background: transparent;
  border: ${({ $debug }) =>
    $debug ? '1px solid rgba(220, 0, 0, 0.55)' : 'none'};
  border-radius: 1px;

  &::selection {
    background: rgba(100, 149, 237, 0.4);
    color: ${({ $debug }) =>
      $debug ? 'rgba(180, 0, 0, 0.85)' : 'transparent'};
  }
`
